import {
  computed,
  effect,
  inject,
  Injectable,
  Renderer2,
  RendererFactory2,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  private readonly loadingState: WritableSignal<boolean> = signal(false);
  public readonly loading: Signal<boolean> = computed(() => this.loadingState());
  private readonly renderer!: Renderer2;
  private container?: HTMLElement;
  private readonly router = inject(Router);

  public constructor(
    private readonly renderFactory: RendererFactory2,
  ) {
    this.renderer = this.renderFactory.createRenderer(null, null);

    // Auto-cancel loading on navigation
    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => {
        if (this.loadingState()) {
          this.stop();
        }
      });

    // React to loading state changes with effect
    effect(() => {
      const isLoading = this.loadingState();
      if (isLoading) {
        this.injectLoader();
      } else {
        this.removeLoader();
      }
    });
  }

  public start(): void {
    this.loadingState.set(true);
  }

  public stop(): void {
    this.loadingState.set(false);
  }

  private injectLoader(): void {
    if (this.container) return;

    this.container = this.renderer.createElement('div');
    if (this.container) {
      this.container.classList.add(
        'fixed', 'right-0', 'left-0', 'top-0', 'w-full',
        'bg-gray-100', 'dark:bg-dark-hover', 'h-2', 'animate-fadein', 'overflow-hidden', 'z-50'
      );

      const bar = this.renderer.createElement('div');
      bar.classList.add(
        'bg-theme-primary', 'h-full', 'animate-progress',
        'absolute', 'h-2', 'left-0', 'w-48'
      );
      this.container.appendChild(bar);

      document.body.appendChild(this.container);
    }
  }

  private removeLoader(): void {
    if (this.container) {
      this.container.classList.add('animate-fadeout');

      setTimeout(() => {
        if (this.container && document.body.contains(this.container)) {
          document.body.removeChild(this.container);
        }
        this.container = undefined;
      }, 250);
    }
  }

  public reset(): void {
    this.stop();
  }
}
